import { AfterViewInit, ChangeDetectionStrategy, Component, Injector, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { DepartmentService, IBaseDepartment, IDepartments } from '@shared/services/department.service';
import { MesEmployeeTransferService } from '@shared/services/export-import-services/mes-employee';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { EmployeeService, IMesUser, IMesUsers } from '@shared/services/mesuser.service';
import { NotificationService } from '@shared/services/notification.service';
import { ITeamProfile, ITeamProfiles, TeamProfileService } from '@shared/services/teamProfiles.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { CreateUpdateEmployeeDialogComponent } from './create-update-employee/create-update-employee';

interface ITableViewModelItem {
	isActive: boolean;
	alias: string;
	department: string;
	employee: IMesUser;
	name: string;
	team: string;
	firstWorkDate: string;
}

@Component({
	selector: 'app-employee',
	templateUrl: './employee.component.html',
	styleUrls: ['./employee.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EmployeeComponent implements AfterViewInit, OnDestroy {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['name', 'department', 'team', 'firstWorkDate', 'isActive', 'menu'];

	dataSource = new MatTableDataSource<ITableViewModelItem>();
	showSearch = false;
	isLoading = true;
	public showInactive = false;
	public viewModel: ITableViewModelItem[] = [];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.users.newbtn',
			click: () => this.onEdit(null),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.users.import.button',
			click: () => this.onImport(),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'mes.administration.users.export.button',
			click: () => this.onExport(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private employeeService: EmployeeService,
		private teamProfileService: TeamProfileService,
		private departmentService: DepartmentService,
		private notificationService: NotificationService,
		private translator: TranslateService,
		private employeeTransferService: MesEmployeeTransferService,
		private injector: Injector,
	) {}

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private getTeam(user: IMesUser, profiles: ITeamProfile[]): string {
		const team = profiles.find((profile) => profile.id === user.teamProfileId);
		if (team) {
			return `(${team.alias}) ${team.description}`;
		}
		return '';
	}

	private getDepartment(user: IMesUser, departments: IBaseDepartment[]): string {
		const department = departments.find((department) => department.id === user.departmentId);
		if (department) {
			return department.alias;
		}
		return '';
	}

	private getITableViewModelItem(user: IMesUser, [profiles, departments]: [ITeamProfiles, IDepartments, IMesUsers]): ITableViewModelItem {
		return {
			isActive: user.isActive,
			alias: user.alias,
			department: this.getDepartment(user, departments.result),
			employee: user,
			name: `${user.firstName} ${user.lastName}`,
			team: this.getTeam(user, profiles.result),
			firstWorkDate: user.firstWorkDate || '',
		};
	}

	private getTableViewModel(data: [ITeamProfiles, IDepartments, IMesUsers]): ITableViewModelItem[] {
		return data[2].result.map((user) => this.getITableViewModelItem(user, data));
	}

	updateList() {
		this.isLoading = true;
		forkJoin([this.teamProfileService.getTeamProfiles(), this.departmentService.getDepartments(), this.employeeService.getEmployees()]).subscribe((data) => {
			this.viewModel = this.getTableViewModel(data);
			this.dataSource.data = this.getFilteredITableViewModelItems();
			this.dataSource.sort = this.sort;
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	private getFilteredITableViewModelItems(): ITableViewModelItem[] {
		return this.showInactive ? this.viewModel : this.viewModel.filter((item) => item.isActive);
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	onEdit(user: IMesUser | null) {
		const dialogRef = this.dialog.open(CreateUpdateEmployeeDialogComponent, {
			disableClose: true,
			width: '550px',
			data: user,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	toggleShowInactive() {
		this.showInactive = !this.showInactive;
		this.dataSource.data = this.getFilteredITableViewModelItems();
	}

	onToggleActive(user: IMesUser) {
		user.isActive = !user.isActive;
		this.employeeService.updateEmployee(user).subscribe(() => {
			this.updateList();
		});
	}

	onDelete(employee: IMesUser) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('mes.administration.users.delete.confirmation-header'),
				content: this.translator.instant('mes.administration.users.delete.confirmation-content', {
					alias: employee.alias,
					firstName: employee.firstName,
					lastName: employee.lastName,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.employeeService.deleteEmployee(employee.id).subscribe({
						next: () => {
							this.notificationService.notify(
								['mes.administration.users.delete.notification-header'],
								[
									'mes.administration.users.delete.notification-content',
									{
										alias: employee.alias,
										firstName: employee.firstName,
										lastName: employee.lastName,
									},
								],
								'success',
							);
							this.updateList();
						},
						error: (err) => {
							if (err.error.error.message.includes('associated timestamps')) {
								this.notificationService.notify(
									['mes.administration.users.delete.notification-error-header'],
									[
										'mes.administration.users.delete.errors.associated-timestamps',
										{
											alias: employee.alias,
											firstName: employee.firstName,
											lastName: employee.lastName,
										},
									],
									'error',
								);
							} else {
								this.notificationService.notify(
									['mes.administration.users.delete.notification-error-header'],
									[
										'mes.administration.users.delete.notification-error-content',
										{
											alias: employee.alias,
											firstName: employee.firstName,
											lastName: employee.lastName,
										},
									],
									'error',
								);
								this.updateList();
							}
						},
					});
				}
			});
	}

	onExport() {
		const employee = this.getFilteredITableViewModelItems().map((item) => item.employee);

		this.employeeTransferService.exportData(employee).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.users.export.notification.success.header'], ['mes.administration.users.export.notification.success.content'], 'success');
			},
			error: (error) => {
				console.error('Failed to export data', error);
				this.notificationService.notify(['mes.administration.users.export.notification.error.header'], ['mes.administration.users.export.notification.error.content'], 'error');
			},
		});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.users.import.title',
				subHeadingKey: 'mes.administration.users.import.subtitle',
				confirmButtonKey: 'mes.administration.users.import.button',
				notificationHeader: 'mes.administration.users.import.notification.success.header',
				notificationContent: 'mes.administration.users.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.users.import.notification.error.header',
				notificationErrorContent: 'mes.administration.users.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.users.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.users.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.employeeTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.users.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.updateList();
		});
	}
}
